// Who takes an AOTD IRL frame, and how the boards group the marked rows.
// The yearly AOTD leaderboard top 3 each take a frame ("leaderboard"); the
// game's frame goes to the best game player outside that top 3 ("game").

import type { GameFrames, StandingsRow } from "../api/types";

export type FrameKind = "leaderboard" | "game";

/** The year the prize rule is about, when the backend has not said (older backend). */
export const FRAME_YEAR = 2026;

/** The 2026 top 3 is final once the 31 December AOTD day ends. */
export const FRAME_CUTOFF_MS = Date.parse("2027-01-01T04:50:00Z");

/** Labels on the game's own boards (home board and /game standings). */
export const GAME_BOARD_LABELS: Record<FrameKind, string> = {
  leaderboard: "getting one from the AOTD leaderboard",
  game: "will get the game's AOTD IRL",
};

export const PROVISIONAL_NOTE = "Frames are provisional until 31 December.";

/** Provisional per the backend, or by the clock when the backend has not said. */
export function framesProvisional(frames: GameFrames | null | undefined, nowMs: number = Date.now()): boolean {
  if (frames && typeof frames.provisional === "boolean") return frames.provisional;
  return nowMs < FRAME_CUTOFF_MS;
}

/** A standings row's mark. The game frame wins if a row somehow carries both. */
export function rowFrameKind(row: StandingsRow, frames: GameFrames | null | undefined): FrameKind | null {
  const taker = frames?.gameTaker?.username;
  if (row.gameFrame === true || (taker != null && taker === row.username)) return "game";
  const top = frames?.leaderboardTop ?? [];
  if (row.frameFromLeaderboard === true || top.some((p) => p.username === row.username)) return "leaderboard";
  return null;
}

/**
 * A row's mark on the AOTD Authors Leaderboard. With frames from the backend,
 * the top 3 is the backend's leaderboardTop (matched by username), the same
 * list the game board and the game's frame-taker come from, so the two boards
 * agree even when a tie at 3rd sorts differently in the two queries. Without
 * frames (older backend, game API down) the board's own first three rows are
 * the top 3.
 */
export function aotdRowFrameKind(
  username: string,
  index: number,
  frames: GameFrames | null | undefined,
): FrameKind | null {
  if (frames && Array.isArray(frames.leaderboardTop)) {
    if (frames.leaderboardTop.some((p) => p.username === username)) return "leaderboard";
    return frames.gameTaker?.username === username ? "game" : null;
  }
  return index < 3 ? "leaderboard" : null;
}

/**
 * Sort for the AOTD Authors Leaderboard: most posts first, and on equal posts
 * the backend's top 3 first, in its order, so a tie at 3rd shows the frame
 * row above the one that misses out. Other ties keep the order they came in.
 */
export function aotdPostsOrder(frames: GameFrames | null | undefined) {
  const top = (frames?.leaderboardTop ?? []).map((p) => p.username);
  const at = (username: string) => {
    const i = top.indexOf(username);
    return i === -1 ? top.length : i;
  };
  return (a: { _id: unknown; postsCount: number }, b: { _id: unknown; postsCount: number }): number =>
    b.postsCount - a.postsCount || at(String(a._id)) - at(String(b._id));
}

/**
 * One row's slice of a bracket. A bracket spans a run of adjacent rows of the
 * same kind: the first row draws the top tick, the last the bottom tick. The
 * label sits on the run's middle row ("middle"), or on the boundary between
 * the two middle rows of an even run ("bottom"). Only the first run of each
 * kind is labelled, so split runs don't repeat the text.
 */
export interface BracketPiece {
  kind: FrameKind;
  top: boolean;
  bottom: boolean;
  label: string | null;
  labelAt: "middle" | "bottom";
}

export function bracketPieces(
  kinds: Array<FrameKind | null>,
  labels: Record<FrameKind, string>,
): Array<BracketPiece | null> {
  const out: Array<BracketPiece | null> = kinds.map(() => null);
  const labelled = new Set<FrameKind>();
  let i = 0;
  while (i < kinds.length) {
    const kind = kinds[i];
    if (!kind) {
      i += 1;
      continue;
    }
    let end = i;
    while (end + 1 < kinds.length && kinds[end + 1] === kind) end += 1;
    const n = end - i + 1;
    const anchor = i + Math.floor((n - 1) / 2);
    const withLabel = !labelled.has(kind);
    labelled.add(kind);
    for (let r = i; r <= end; r += 1) {
      out[r] = {
        kind,
        top: r === i,
        bottom: r === end,
        label: withLabel && r === anchor ? labels[kind] : null,
        labelAt: n % 2 === 1 ? "middle" : "bottom",
      };
    }
    i = end + 1;
  }
  return out;
}
